/**
 * DAG Validation and Topological Sorting
 *
 * Validates workflow definitions to ensure they form valid directed acyclic graphs (DAGs)
 * and performs topological sorting for execution order.
 *
 * Uses Kahn's algorithm for topological sorting and cycle detection.
 *
 * @packageDocumentation
 */

import type { WorkflowDefinition, WorkflowStep } from '../types/workflow';
import { WorkflowDefinitionError, WorkflowDependencyError } from '../types/errors';

/**
 * Validates a workflow definition and returns steps in topologically sorted order.
 *
 * This is the main entry point for DAG validation. It performs all necessary
 * validations and returns steps ready for execution.
 *
 * @param workflow - The workflow definition to validate
 * @returns Array of steps in execution order (dependencies before dependents)
 * @throws {WorkflowDefinitionError} If workflow structure is invalid
 * @throws {WorkflowDependencyError} If there are circular dependencies
 *
 * @example
 * ```typescript
 * const workflow: WorkflowDefinition = {
 *   workflowName: 'invoice-processor',
 *   steps: [
 *     { stepName: 'extract', model: 'gpt-4o' },
 *     { stepName: 'summarize', model: 'gpt-4o', dependsOn: ['extract'] }
 *   ]
 * };
 *
 * const sortedSteps = validateAndSort(workflow);
 * // Returns: [extractStep, summarizeStep]
 * ```
 */
export function validateAndSort(workflow: WorkflowDefinition): WorkflowStep[] {
  // Validate basic workflow structure
  if (!workflow.workflowName || workflow.workflowName.trim() === '') {
    throw new WorkflowDefinitionError('Workflow name must be a non-empty string');
  }

  if (!Array.isArray(workflow.steps) || workflow.steps.length === 0) {
    throw new WorkflowDefinitionError('Workflow must have at least one step');
  }

  // Validate no duplicate step names
  validateUniqueStepNames(workflow.steps);

  // Validate all dependencies exist and no self-dependencies
  validateDependencies(workflow.steps);

  // Perform topological sort (also detects cycles)
  return topologicalSort(workflow.steps);
}

/**
 * Validates that all step names are unique within the workflow.
 *
 * @param steps - Array of workflow steps
 * @throws {WorkflowDefinitionError} If duplicate step names are found
 */
function validateUniqueStepNames(steps: WorkflowStep[]): void {
  const stepNames = new Set<string>();
  const duplicates: string[] = [];

  for (const step of steps) {
    if (!step.stepName || step.stepName.trim() === '') {
      throw new WorkflowDefinitionError('All steps must have a non-empty stepName');
    }

    if (stepNames.has(step.stepName)) {
      duplicates.push(step.stepName);
    } else {
      stepNames.add(step.stepName);
    }
  }

  if (duplicates.length > 0) {
    throw new WorkflowDefinitionError(
      `Duplicate step names found: ${duplicates.join(', ')}`
    );
  }
}

/**
 * Validates that all dependencies reference existing steps and no self-dependencies exist.
 *
 * @param steps - Array of workflow steps
 * @throws {WorkflowDefinitionError} If dependencies are invalid
 */
function validateDependencies(steps: WorkflowStep[]): void {
  // Build a set of all valid step names
  const stepNames = new Set(steps.map((s) => s.stepName));

  for (const step of steps) {
    // Validate model is provided
    if (!step.model || step.model.trim() === '') {
      throw new WorkflowDefinitionError(`Step "${step.stepName}" is missing required field "model"`);
    }

    // Check dependencies if present
    if (step.dependsOn && step.dependsOn.length > 0) {
      for (const dependency of step.dependsOn) {
        // Check for self-dependency
        if (dependency === step.stepName) {
          throw new WorkflowDefinitionError(
            `Step "${step.stepName}" cannot depend on itself`
          );
        }

        // Check for missing dependency
        if (!stepNames.has(dependency)) {
          throw new WorkflowDefinitionError(
            `Step "${step.stepName}" depends on unknown step "${dependency}"`
          );
        }
      }
    }
  }
}

/**
 * Performs topological sorting using Kahn's algorithm.
 *
 * This algorithm:
 * 1. Finds all nodes with no incoming edges (no dependencies)
 * 2. Adds them to the result and removes their outgoing edges
 * 3. Repeats until all nodes are processed or a cycle is detected
 *
 * @param steps - Array of workflow steps
 * @returns Steps sorted in topological order
 * @throws {WorkflowDependencyError} If a circular dependency is detected
 *
 * @remarks
 * Time complexity: O(V + E) where V is vertices (steps) and E is edges (dependencies)
 */
export function topologicalSort(steps: WorkflowStep[]): WorkflowStep[] {
  // Build adjacency information
  const stepMap = new Map<string, WorkflowStep>();
  const inDegree = new Map<string, number>();
  const dependents = new Map<string, Set<string>>();

  // Initialize data structures
  for (const step of steps) {
    stepMap.set(step.stepName, step);
    inDegree.set(step.stepName, 0);
    dependents.set(step.stepName, new Set());
  }

  // Build the graph
  for (const step of steps) {
    if (step.dependsOn && step.dependsOn.length > 0) {
      // This step depends on others, so it has incoming edges
      inDegree.set(step.stepName, step.dependsOn.length);

      // Add this step as a dependent of each dependency
      for (const dependency of step.dependsOn) {
        const depSet = dependents.get(dependency);
        if (depSet) {
          depSet.add(step.stepName);
        }
      }
    }
  }

  // Find all steps with no dependencies (in-degree = 0)
  const queue: string[] = [];
  for (const [stepName, degree] of inDegree.entries()) {
    if (degree === 0) {
      queue.push(stepName);
    }
  }

  const sorted: WorkflowStep[] = [];
  const visited = new Set<string>();

  // Process queue
  while (queue.length > 0) {
    // Remove a step with no dependencies
    const currentName = queue.shift()!;
    const currentStep = stepMap.get(currentName)!;
    sorted.push(currentStep);
    visited.add(currentName);

    // Process all steps that depend on the current step
    const deps = dependents.get(currentName);
    if (deps) {
      for (const dependentName of deps) {
        // Decrease in-degree (remove the edge)
        const degree = inDegree.get(dependentName)! - 1;
        inDegree.set(dependentName, degree);

        // If in-degree becomes 0, add to queue
        if (degree === 0) {
          queue.push(dependentName);
        }
      }
    }
  }

  // If we haven't visited all steps, there's a cycle
  if (sorted.length !== steps.length) {
    const unvisited = steps
      .filter((s) => !visited.has(s.stepName))
      .map((s) => s.stepName);

    // Find a cycle to report it
    const cycle = findCycle(steps, unvisited);
    throw new WorkflowDependencyError(
      `Circular dependency detected: ${cycle.join(' → ')}`
    );
  }

  return sorted;
}

/**
 * Finds and returns a cycle in the dependency graph.
 *
 * Used for error reporting when a cycle is detected.
 *
 * @param steps - All workflow steps
 * @param unvisited - Steps that weren't processed (contain cycles)
 * @returns Array of step names forming a cycle
 */
function findCycle(steps: WorkflowStep[], unvisited: string[]): string[] {
  const stepMap = new Map(steps.map((s) => [s.stepName, s]));
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const path: string[] = [];

  function dfs(stepName: string): boolean {
    if (visiting.has(stepName)) {
      // Found a cycle
      return true;
    }

    if (visited.has(stepName)) {
      return false;
    }

    visiting.add(stepName);
    path.push(stepName);

    const step = stepMap.get(stepName);
    if (step?.dependsOn) {
      for (const dep of step.dependsOn) {
        if (dfs(dep)) {
          return true;
        }
      }
    }

    visiting.delete(stepName);
    visited.add(stepName);
    path.pop();
    return false;
  }

  // Start DFS from unvisited nodes
  for (const startNode of unvisited) {
    if (dfs(startNode)) {
      // Return the path that contains the cycle
      // The last element in path is part of the cycle
      return [...path, startNode];
    }
  }

  // Fallback - shouldn't reach here if there's actually a cycle
  return unvisited;
}

/**
 * Detects if there are any cycles in the workflow dependency graph.
 *
 * @param steps - Array of workflow steps
 * @returns True if a cycle exists, false otherwise
 *
 * @remarks
 * This is a helper function primarily used for testing.
 * In production, use validateAndSort which throws on cycles.
 */
export function detectCycles(steps: WorkflowStep[]): boolean {
  try {
    topologicalSort(steps);
    return false;
  } catch (error) {
    if (error instanceof WorkflowDependencyError) {
      return true;
    }
    throw error;
  }
}
